import { useCallback, useState } from 'react';
import {
  faArrowRightFromBracket,
  faArrowsRepeat,
  faGear,
  faShuffle,
} from '@fortawesome/pro-light-svg-icons';
import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { queryString } from '@olivierpascal/helpers';
import { Badge, Menu, MenuItem } from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiGetUserResponseBody } from '~/lib/api/modules/Users';
import { UserAvatar } from '~/components/atoms/UserAvatar';
import { LogInAsFormDialog } from '~/components/forms/LogInAsForm';
import { NavMenuItem } from '~/components/molecules/NavMenu';
import { useNavigate } from '~/components/utils/Navigation';
import { useAllCredentialsQuery } from '~/lib/auth';

export type ISidebarUserMenuProps = IContainerProps & {
  name?: string;
  email?: string;
  isSuperAdmin?: boolean;
  user?: IApiGetUserResponseBody;
};

const itemStyles = stylex.create({
  label: {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: '2',
    wordBreak: 'break-all',
  },
  supportingText: {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: '1',
    wordBreak: 'break-all',
  },
});

export const SidebarUserMenu: React.FC<ISidebarUserMenuProps> = (props) => {
  const { sx, user, name, email, isSuperAdmin } = props;
  const { t } = useTranslation();
  const allCredentialsQuery = useAllCredentialsQuery();
  const location = useLocation();
  const navigate = useNavigate();
  const [logInAsFormDialogOpened, setLogInAsFormDialogOpened] = useState(false);
  const multiAccountsCount = allCredentialsQuery.data?.length ?? 0;

  const handleLogOut = useCallback((): void => {
    // eslint-disable-next-line no-alert
    const doLogOut = confirm(
      multiAccountsCount > 1
        ? t('SidebarUserMenu.dialogs.confirmMultiLogOut.text')
        : t('SidebarUserMenu.dialogs.confirmSingleLogOut.text'),
    );

    if (doLogOut) {
      navigate('^/logout');
    }
  }, [multiAccountsCount, navigate, t]);

  return (
    <>
      <LogInAsFormDialog
        opened={logInAsFormDialogOpened}
        onClose={() => setLogInAsFormDialogOpened(false)}
      />

      <Menu
        trigger={({ getProps }) => (
          <NavMenuItem
            leading={<UserAvatar user={user} isSuperAdmin={isSuperAdmin} />}
            trailingIcon={<FontAwesomeIcon icon={faEllipsisVertical} />}
            supportingText={email}
            innerStyles={{ item: itemStyles }}
            sx={sx}
            {...getProps()}
          >
            {name}
          </NavMenuItem>
        )}
        placement="top-end"
      >
        <MenuItem
          label={t('SidebarUserMenu.items.updateProfile.label')}
          href="/me/update"
          leadingIcon={<FontAwesomeIcon icon={faGear} />}
        />
        <MenuItem
          label={t('SidebarUserMenu.items.switchSession.label')}
          href={`^/sessions?${queryString.build({
            previousUrl: `${location.pathname}${location.search}`,
          })}`}
          leadingIcon={<FontAwesomeIcon icon={faArrowsRepeat} />}
          trailing={
            multiAccountsCount > 1 ? (
              <Badge value={multiAccountsCount} />
            ) : undefined
          }
        />
        {isSuperAdmin ? (
          <MenuItem
            label={t('SidebarUserMenu.items.logInAs.label')}
            onClick={() => setLogInAsFormDialogOpened(true)}
            leadingIcon={<FontAwesomeIcon icon={faShuffle} />}
          />
        ) : null}
        <MenuItem
          label={t('common.logMeOut')}
          onClick={() => handleLogOut()}
          leadingIcon={<FontAwesomeIcon icon={faArrowRightFromBracket} />}
          variant="danger"
        />
      </Menu>
    </>
  );
};
